/**
 * 單篇上傳頁(UploadEssay)的圖片狀態:型別與幾段狀態轉換(kanban 1003418 從元件抽出)。
 *
 * 這裡的轉換原本都寫在 setImageState 的 updater 裡,逐值等價搬出來,元件改成呼叫它們。
 * 不碰 React、不呼叫 URL.revokeObjectURL —— 要釋放的 URL 以 revokeUrls 回傳,
 * 由呼叫端在 updater 裡照舊釋放。
 *
 * 抽出來的理由:原本只能用讀原始碼字串的方式測接線,抓不到「換張後陣列長度與索引
 * 對不上」這類錯(第 2 張在分割編輯器按「上一步」,索引指到 undefined、視窗一片空白)。
 * 搬成純函式後,uploadImageState.test.ts 可以把整段操作串起來逐值比對。
 */
import {
  nextCorrectionGroupId,
  buildCorrectionEditor,
  resolveCancelledGroupIndex,
} from "./correctionQueue";

// 類型定義
export interface CornerPoint {
  x: number;
  y: number;
}

export interface PerspectivePoints {
  topLeft: CornerPoint;
  topRight: CornerPoint;
  bottomLeft: CornerPoint;
  bottomRight: CornerPoint;
}

// 單張原始圖片的處理結果
export interface ProcessedImageGroup {
  id: string; // 唯一標識符
  originalFile: File;
  originalUrl: string;
  correctedUrl?: string; // 校正後的完整圖片URL
  processedFiles: File[];
  processedUrls: string[];
  isProcessed: boolean; // 是否已經完成校正和分割
}

// 圖片狀態接口
export interface ImageState {
  selectedImages: File[];
  originalSelectedImages: File[]; // 儲存原始圖片文件，不會被透視校正覆蓋
  previewUrls: string[];
  originalPreviewUrls: string[]; // 儲存原始圖片的預覽 URL，不會被透視校正覆蓋
  imageError: string | null;
  showPerspectiveEditor: boolean;
  showCropEditor: boolean;
  currentEditingImageIndex: number;
  currentProcessingGroupId: string | null; // 當前正在處理的群組ID
  // 旋轉視窗還沒處理完的群組 id,依上傳順序(kanban 106497)。
  // 一份多頁 PDF 一次帶進多個 group,視窗要逐張走完;原本只處理最新那張,
  // 第 1 頁沒機會轉正就被交出去。只在旋轉視窗動線(rotateOnlyFlow)使用,
  // 舊的四角校正動線一律留空、行為不變。
  pendingRotationGroupIds: string[];
  // 調整邊界 → 分割編輯器還沒走完的群組 id,依上傳順序,含當下那張(kanban 1003418)。
  // 一份兩頁 PDF 一次帶進兩個 group,原本視窗只開最後一張,第 1 頁沒有入口校正分段、
  // 送出時也不會被收進去。分段完成或按 X 取消一張就接著開下一張,清空才關窗。
  // 只在四角校正動線使用;旋轉動線走上面的 pendingRotationGroupIds。
  pendingCorrectionGroupIds: string[];
  perspectivePointsArray: (PerspectivePoints | null)[];
  processedImageUrlArray: (string | null)[];
  croppedImages: string[];
  croppedBlobs: Blob[];
  needsInitialization: boolean;
  originalImages: File[]; // 儲存原始上傳的圖片
  originalImageCount: number; // 追蹤用戶實際上傳的原始圖片數量
  processedGroups: ProcessedImageGroup[]; // 每張原始圖片的處理結果組
  perspectiveManuallyAdjusted: boolean[]; // 追蹤每張圖片是否被使用者手動調整過透視校正點
  // 預覽區每組圖片的旋轉角度(0/90/180/270),key = ProcessedImageGroup.id。
  // 提交時會用 bakeRotationIntoImage 烤進圖檔像素(kanban 113663);
  // 放進 ImageState 而非元件 local state,是為了讓「產生新校正輸出就歸零」能與
  // 群組更新在同一次 setImageState 內原子完成,不會有先後順序的競態。
  imageRotations: Record<string, number>;
}

/** 狀態轉換的結果:新狀態,加上呼叫端要在 updater 裡釋放的物件網址。 */
export interface ImageStateTransition {
  state: ImageState;
  revokeUrls: string[];
}

/** 上傳頁一開始(還沒有任何圖)的狀態。 */
export function createInitialImageState(): ImageState {
  return {
    selectedImages: [],
    originalSelectedImages: [],
    previewUrls: [],
    originalPreviewUrls: [],
    imageError: null,
    showPerspectiveEditor: false,
    showCropEditor: false,
    currentEditingImageIndex: 0,
    currentProcessingGroupId: null,
    pendingRotationGroupIds: [],
    pendingCorrectionGroupIds: [],
    perspectivePointsArray: [],
    processedImageUrlArray: [],
    croppedImages: [],
    croppedBlobs: [],
    needsInitialization: false,
    originalImages: [],
    originalImageCount: 0,
    processedGroups: [],
    perspectiveManuallyAdjusted: [],
    imageRotations: {},
  };
}

// 只保留現存 group 的旋轉角度,避免刪圖/重設後留下孤兒 key
// (group id 是 `group_${Date.now()}_${index}`,同毫秒重建有機會撞號 → 撿到上一輪的角度)
export const pruneRotations = (
  rotations: Record<string, number>,
  groups: ProcessedImageGroup[]
): Record<string, number> => {
  const next: Record<string, number> = {};
  groups.forEach((group) => {
    if (group && rotations[group.id]) next[group.id] = rotations[group.id];
  });
  return next;
};

// 四角校正動線的佇列(kanban 1003418):把狀態切到「下一張的調整邊界視窗」。
// state 是這一步(分段完成 / 取消一張)已套用完的狀態。selectedImages / previewUrls /
// 編輯索引的形狀與「上傳第二張圖片」時 handleImageChange 產生的一致(規則見
// buildCorrectionEditor),currentProcessingGroupId 與編輯索引指同一張,
// setProcessedImageUrl / handleCropComplete 的回寫才會落在正確的 group。
// 找不到下一張(理論上不會)回傳 null,呼叫端照原本的方式關窗。
export const openNextCorrectionEditor = (
  state: ImageState,
  nextId: string,
  pending: string[]
): ImageState | null => {
  const editor = buildCorrectionEditor(state.processedGroups, nextId);
  if (!editor) return null;
  return {
    ...state,
    selectedImages: editor.selectedImages,
    previewUrls: editor.previewUrls,
    currentEditingImageIndex: editor.editingIndex,
    currentProcessingGroupId: nextId,
    pendingCorrectionGroupIds: pending,
    showPerspectiveEditor: true,
    showCropEditor: false,
    // 觸發調整邊界視窗重新初始化角點(含自動預填),與新上傳一張圖時相同
    needsInitialization: true,
    perspectivePointsArray: editor.previewUrls.map(() => null),
    processedImageUrlArray: editor.previewUrls.map(() => null),
    perspectiveManuallyAdjusted: editor.previewUrls.map(() => false),
  };
};

// 調整邊界 / 分割編輯器按 X = 取消「當下這一張」(kanban 1003418),算出移除後的清單。
// 原本一律移除最後一張(slice(0, -1)):一次只加一張時當下那張就是最後一張,結果相同;
// 但多張逐張處理時,第 1 張的視窗按 X 會拿掉還沒處理的第 2 張。
// group 依 currentProcessingGroupId 找(規則見 resolveCancelledGroupIndex);原圖清單
// 依該 group 的 originalUrl 反查位置 ——「重設圖片」只移除 processedGroups 一格,
// 原圖清單與 groups 的位置不保證一一對應。反查不到時退回最後一張,同改動前。
export const removeCancelledCorrectionGroup = (prev: ImageState) => {
  const groupIndex = resolveCancelledGroupIndex(prev.processedGroups, prev.currentProcessingGroupId);
  const cancelled = prev.processedGroups[groupIndex];
  const urlIndex = cancelled ? prev.originalPreviewUrls.indexOf(cancelled.originalUrl) : -1;
  const originalIndex = urlIndex >= 0 ? urlIndex : prev.originalSelectedImages.length - 1;
  return {
    cancelledId: cancelled?.id ?? null,
    // 被移除那張原圖的預覽 URL(呼叫端負責釋放)
    cancelledOriginalUrl: prev.originalPreviewUrls[originalIndex],
    originalSelectedImages: prev.originalSelectedImages.filter((_, i) => i !== originalIndex),
    originalPreviewUrls: prev.originalPreviewUrls.filter((_, i) => i !== originalIndex),
    processedGroups: prev.processedGroups.filter((_, i) => i !== groupIndex),
  };
};

/**
 * 選圖(handleImageChange):新圖片通過張數上限檢查之後的狀態。
 * 原本是 updater 裡 validFiles 非空且未超過 2 張時的那段 return;張數超標、沒有有效檔案
 * 兩條分支與 trackEvent 仍留在元件。createGroupId 由呼叫端傳入(元件傳
 * `group_${Date.now()}_${index}`),測試才能給固定的 id。
 */
export function addUploadedImages(
  prev: ImageState,
  input: {
    validFiles: File[];
    newPreviewUrls: string[];
    imageError: string | null;
    rotateOnlyFlow: boolean;
    skipCorrect: boolean;
    createGroupId: (index: number) => string;
  }
): ImageState {
  const { validFiles, newPreviewUrls, rotateOnlyFlow, skipCorrect, createGroupId } = input;
  const newImageError = input.imageError;

  // 添加新圖片到列表中
  const finalFiles = [...prev.selectedImages, ...validFiles];
  const finalUrls = [...prev.previewUrls, ...newPreviewUrls];

  // 檢查是否有新增的圖片 - 每次新增圖片都要開啟透視校正模式
  const hasNewImages = finalFiles.length > prev.selectedImages.length;
  const newImageIndex = finalFiles.length - 1; // 最新圖片的索引
  // 這批新圖的第一張(kanban 1003418):四角校正動線從這張開始逐張處理。
  // 一次只加一張時與 newImageIndex 相同
  const firstNewImageIndex = prev.selectedImages.length;

  const newGroups: ProcessedImageGroup[] = hasNewImages ? validFiles.map((file, index) => ({
    id: createGroupId(index), // 生成唯一ID
    originalFile: file,
    originalUrl: newPreviewUrls[index],
    processedFiles: [],
    processedUrls: [],
    isProcessed: false,
  })) : [];

  return {
    selectedImages: finalFiles,
    originalSelectedImages: hasNewImages ? [...prev.originalSelectedImages, ...validFiles] : prev.originalSelectedImages,
    previewUrls: finalUrls,
    originalPreviewUrls: hasNewImages ? [...prev.originalPreviewUrls, ...newPreviewUrls] : prev.originalPreviewUrls,
    imageError: newImageError,
    showPerspectiveEditor: hasNewImages && !skipCorrect ? true : prev.showPerspectiveEditor,
    showCropEditor: prev.showCropEditor,
    // 四角校正動線的編輯索引指向這批的第一張(kanban 1003418):
    // 調整邊界視窗從 previewUrls[currentEditingImageIndex] 取圖,
    // 要與下面的 currentProcessingGroupId(校正輸出回寫的 group)指同一張。
    // 旋轉視窗不靠這個索引取圖(見 resolveRotationSource),維持原值。
    currentEditingImageIndex: hasNewImages
      ? (rotateOnlyFlow ? newImageIndex : firstNewImageIndex)
      : prev.currentEditingImageIndex,
    // 兩條動線都從「第一張」開始,並把整批放進各自的佇列逐張走完
    // (旋轉 kanban 106497、四角校正+分段 kanban 1003418)。
    // 四角校正動線原本是「最新那張」,兩頁 PDF 的第 1 頁因此沒有入口。
    currentProcessingGroupId: hasNewImages && newGroups.length > 0
      ? newGroups[0].id
      : prev.currentProcessingGroupId,
    pendingRotationGroupIds: hasNewImages && rotateOnlyFlow
      ? newGroups.map((group) => group.id)
      : prev.pendingRotationGroupIds,
    pendingCorrectionGroupIds: hasNewImages && !rotateOnlyFlow
      ? newGroups.map((group) => group.id)
      : prev.pendingCorrectionGroupIds,
    perspectivePointsArray: finalFiles.map((_, index) =>
      index < prev.perspectivePointsArray.length ? prev.perspectivePointsArray[index] : null
    ),
    processedImageUrlArray: finalFiles.map((_, index) =>
      index < prev.processedImageUrlArray.length ? prev.processedImageUrlArray[index] : null
    ),
    croppedImages: prev.croppedImages,
    croppedBlobs: prev.croppedBlobs,
    needsInitialization: hasNewImages,
    originalImages: hasNewImages ? [...prev.originalImages, ...validFiles] : prev.originalImages,
    originalImageCount: hasNewImages ? prev.originalImageCount + validFiles.length : prev.originalImageCount,
    processedGroups: hasNewImages ? [
      ...prev.processedGroups,
      ...newGroups
    ] : prev.processedGroups,
    perspectiveManuallyAdjusted: finalFiles.map((_, index) =>
      index < prev.perspectiveManuallyAdjusted.length ? prev.perspectiveManuallyAdjusted[index] : false
    ),
    // 新上傳的 group 沒有角度紀錄(=0);同時清掉已不存在的 group 殘留角度
    imageRotations: pruneRotations(
      prev.imageRotations,
      hasNewImages ? [...prev.processedGroups, ...newGroups] : prev.processedGroups
    ),
  };
}

/**
 * 調整邊界(closing = "perspective")或分割編輯器(closing = "crop")按 X,
 * 原始圖片數量大於 1 的那條分支:移除「當下這一張」;同一批還有沒處理的圖就接著開下一張
 * (kanban 1003418)。只有一張時的「全部清空」分支留在元件。
 * 兩個視窗的差別只在關掉的是哪一個旗標,其餘逐值相同。
 */
export function cancelCurrentCorrection(
  prev: ImageState,
  closing: "perspective" | "crop"
): ImageStateTransition {
  // 移除當下這張原始圖片相關的資源(一次只加一張時就是最後一張,同改動前)
  const removed = removeCancelledCorrectionGroup(prev);

  // 釋放這張原始圖片的 URL
  const revokeUrls = removed.cancelledOriginalUrl ? [removed.cancelledOriginalUrl] : [];

  const newOriginalSelectedImages = removed.originalSelectedImages;
  const newOriginalPreviewUrls = removed.originalPreviewUrls;
  const newProcessedGroups = removed.processedGroups;

  const closed: ImageState = {
    ...prev,
    ...(closing === "perspective" ? { showPerspectiveEditor: false } : { showCropEditor: false }),
    originalSelectedImages: newOriginalSelectedImages,
    originalPreviewUrls: newOriginalPreviewUrls,
    originalImageCount: prev.originalImageCount - 1,
    processedGroups: newProcessedGroups,
    // 重置相關狀態
    currentEditingImageIndex: 0,
    selectedImages: newOriginalSelectedImages,
    previewUrls: newOriginalPreviewUrls,
    processedImageUrlArray: newOriginalPreviewUrls.map(() => null),
    perspectivePointsArray: newOriginalPreviewUrls.map(() => null),
    perspectiveManuallyAdjusted: newOriginalPreviewUrls.map(() => false),
    // 被移除的 group 連同它的旋轉角度一起清掉
    imageRotations: pruneRotations(prev.imageRotations, newProcessedGroups),
    pendingCorrectionGroupIds: [],
  };

  // 同一批還有沒處理的圖 → 接著開下一張的調整邊界視窗
  const next = nextCorrectionGroupId(prev.pendingCorrectionGroupIds, newProcessedGroups, removed.cancelledId);
  if (next.nextId) {
    const opened = openNextCorrectionEditor(closed, next.nextId, next.pending);
    if (opened) return { state: opened, revokeUrls };
  }
  return { state: closed, revokeUrls };
}

/**
 * 調整邊界視窗按「下一步」後,校正圖寫回目前編輯的那一格(applyProcessedImage)。
 * file / url 是校正圖轉成的檔案與物件網址(元件在 fetch 完成後才呼叫,所以是非同步寫回)。
 *
 * groupId:按「下一步」當下正在校正的 group(kanban 1003418)。寫的位置是寫回當下的
 * currentEditingImageIndex;多張逐張處理時若這段期間已換到下一張,照寫會把這張的校正圖
 * 蓋到下一張、還把下一張的視窗關掉 —— 換張了就放棄寫回,新的 url 交給呼叫端釋放。
 */
export function applyCorrectedImage(
  prev: ImageState,
  input: { groupId: string | null; file: File; url: string }
): ImageStateTransition {
  const { groupId, file, url: newUrl } = input;
  if (prev.currentProcessingGroupId !== groupId) {
    return { state: prev, revokeUrls: [newUrl] };
  }

  const currentIndex = prev.currentEditingImageIndex;
  const revokeUrls: string[] = [];

  // 釋放舊的 URL —— 但這張原圖的預覽 URL 不能放(kanban 1003418):分割編輯器
  // 按「上一步」會用同一個 URL(group.originalUrl)重畫調整邊界視窗。原圖 URL
  // 在移除這張圖(按 X、重設圖片)或離開頁面時才釋放。
  const oldUrl = prev.previewUrls[currentIndex];
  if (oldUrl && !prev.originalPreviewUrls.includes(oldUrl)) {
    revokeUrls.push(oldUrl);
  }

  const newSelectedImages = [...prev.selectedImages];
  const newPreviewUrls = [...prev.previewUrls];
  const newPerspectivePointsArray = [...prev.perspectivePointsArray];
  const newProcessedImageUrlArray = [...prev.processedImageUrlArray];

  newSelectedImages[currentIndex] = file;
  newPreviewUrls[currentIndex] = newUrl;
  newPerspectivePointsArray[currentIndex] = null;
  newProcessedImageUrlArray[currentIndex] = null;

  return {
    state: {
      ...prev,
      selectedImages: newSelectedImages,
      previewUrls: newPreviewUrls,
      showPerspectiveEditor: false,
      perspectivePointsArray: newPerspectivePointsArray,
      processedImageUrlArray: newProcessedImageUrlArray
    },
    revokeUrls,
  };
}

/**
 * 分割編輯器回傳分段結果(handleCropComplete)。files / urls 已依中文由右到左反轉好。
 *
 * groupId:分割編輯器開啟時正在處理的 group(kanban 1003418)。分段結果是非同步回來的,
 * 期間「確認分段」可再按一次(連點)、也可按 X;換張了或那張已被取消就丟掉這次結果,
 * 這批細條的 URL 交給呼叫端釋放。
 */
export function applyCropResult(
  prev: ImageState,
  input: { groupId?: string | null; files: File[]; urls: string[] }
): ImageStateTransition {
  const { groupId, files: imageFiles, urls: imageUrls } = input;
  if (
    groupId &&
    (prev.currentProcessingGroupId !== groupId ||
      !prev.processedGroups.some((group) => group.id === groupId))
  ) {
    return { state: prev, revokeUrls: [...imageUrls] };
  }

  // 使用 ID-based 系統找到當前正在處理的群組
  let targetGroupIndex = -1;

  if (prev.currentProcessingGroupId) {
    // 根據 ID 找到正確的群組
    targetGroupIndex = prev.processedGroups.findIndex(group => group.id === prev.currentProcessingGroupId);
  }

  // 如果根據ID找不到群組，退回到原有邏輯作為備選
  if (targetGroupIndex === -1) {
    // 找到 isProcessed 為 false 的群組（最近上傳且未處理的）
    for (let i = prev.processedGroups.length - 1; i >= 0; i--) {
      if (!prev.processedGroups[i].isProcessed) {
        targetGroupIndex = i;
        break;
      }
    }

    // 如果所有群組都已處理，則取最後一個群組
    if (targetGroupIndex === -1 && prev.processedGroups.length > 0) {
      targetGroupIndex = prev.processedGroups.length - 1;
    }
  }

  // 更新對應的 ProcessedImageGroup
  const newProcessedGroups = [...prev.processedGroups];
  const newImageRotations = { ...prev.imageRotations };
  if (targetGroupIndex >= 0 && targetGroupIndex < newProcessedGroups.length) {
    newProcessedGroups[targetGroupIndex] = {
      ...newProcessedGroups[targetGroupIndex],
      processedFiles: imageFiles,
      processedUrls: imageUrls,
      isProcessed: true,
    };
    // 校正+分割產生的新輸出已是轉正後的像素 → 這組的預覽旋轉角度歸零,
    // 否則交卷時 bakeRotationIntoImage 會再轉一次(kanban 113663 / 對應國中 108395)
    delete newImageRotations[newProcessedGroups[targetGroupIndex].id];
  }

  // 重新構建 selectedImages 和 previewUrls（包含所有處理後的圖片）
  const allProcessedFiles: File[] = [];
  const allProcessedUrls: string[] = [];

  newProcessedGroups.forEach((group) => {
    if (group.isProcessed) {
      allProcessedFiles.push(...group.processedFiles);
      allProcessedUrls.push(...group.processedUrls);
    }
  });

  // 修復: croppedBlobs 應該累積所有處理的圖片，而不只是當前的
  const finalCroppedBlobs = allProcessedFiles.length > 0 ? allProcessedFiles.map(file => {
    return new Blob([file], { type: file.type });
  }) : prev.croppedBlobs;

  const done: ImageState = {
    ...prev,
    selectedImages: allProcessedFiles.length > 0 ? allProcessedFiles : prev.selectedImages,
    previewUrls: allProcessedUrls.length > 0 ? allProcessedUrls : prev.previewUrls,
    croppedImages: allProcessedUrls.length > 0 ? allProcessedUrls : prev.croppedImages,
    croppedBlobs: finalCroppedBlobs, // 修復: 使用累積的所有圖片
    showCropEditor: false,
    // 重置其他相關狀態
    showPerspectiveEditor: false,
    currentEditingImageIndex: 0,
    currentProcessingGroupId: null,
    perspectivePointsArray: [],
    processedImageUrlArray: [],
    // 保留和更新原始圖片相關狀態
    originalImages: prev.originalImages,
    originalImageCount: prev.originalImageCount,
    processedGroups: newProcessedGroups,
    imageRotations: pruneRotations(newImageRotations, newProcessedGroups),
    pendingCorrectionGroupIds: [],
  };

  // 同一批還有沒處理的圖 → 接著開下一張的調整邊界視窗(kanban 1003418)。
  // 以剛分段完的那個 group 前進,不讀 prev.currentProcessingGroupId:
  // 上面找 group 有 fallback,兩者不保證相同
  const finishedId = newProcessedGroups[targetGroupIndex]?.id ?? prev.currentProcessingGroupId;
  const next = nextCorrectionGroupId(prev.pendingCorrectionGroupIds, newProcessedGroups, finishedId);
  if (next.nextId) {
    const opened = openNextCorrectionEditor(done, next.nextId, next.pending);
    if (opened) return { state: opened, revokeUrls: [] };
  }
  return { state: done, revokeUrls: [] };
}

/**
 * 分割編輯器按「上一步」回到調整邊界視窗(goBackToPerspectiveEditor)。
 *
 * 從這一張的 group 重建調整邊界視窗要的清單與索引(kanban 1003418),形狀與換到下一張時
 * 相同(buildCorrectionEditor)。原本換回 originalSelectedImages / originalPreviewUrls、
 * 索引不動:第 2 張的索引是「第 1 張的細條數」,換回原圖清單(長度 2)後
 * previewUrls[索引] 是 undefined,視窗一片空白,只能按 X 把第 2 張整張移除。
 * 第 1 張只切 1 段時索引剛好是 1 才沒事。第 1 張與單張上傳時,重建出來的仍是同一張
 * 原圖、索引 0,畫面行為不變。
 */
export function returnToCorrectionEditor(prev: ImageState): ImageState {
  // 這張要重新校正 → 它的舊預覽旋轉角度作廢(新的校正輸出會自帶轉正),
  // 但別動其他已完成的 group,那是使用者刻意轉的
  const newImageRotations = { ...prev.imageRotations };
  if (prev.currentProcessingGroupId) delete newImageRotations[prev.currentProcessingGroupId];

  const editor = prev.currentProcessingGroupId
    ? buildCorrectionEditor(prev.processedGroups, prev.currentProcessingGroupId)
    : null;
  if (editor) {
    return {
      ...prev,
      showCropEditor: false,
      showPerspectiveEditor: true,
      selectedImages: editor.selectedImages,
      previewUrls: editor.previewUrls,
      currentEditingImageIndex: editor.editingIndex,
      processedImageUrlArray: editor.previewUrls.map(() => null),
      // 重置透視校正點，讓使用者可以重新進行校正
      perspectivePointsArray: editor.previewUrls.map(() => null),
      perspectiveManuallyAdjusted: editor.previewUrls.map(() => false),
      needsInitialization: true, // 觸發重新初始化透視校正點
      imageRotations: newImageRotations,
    };
  }

  // 找不到這一張的 group(理論上不會):照原本的方式換回原圖清單
  return {
    ...prev,
    showCropEditor: false,
    showPerspectiveEditor: true,
    // 重置到原始圖片，清除校正後的圖片
    selectedImages: [...prev.originalSelectedImages], // 恢復原始文件
    previewUrls: [...prev.originalPreviewUrls], // 恢復原始預覽 URL
    processedImageUrlArray: prev.processedImageUrlArray.map(() => null),
    // 重置透視校正點，讓使用者可以重新進行校正
    perspectivePointsArray: prev.perspectivePointsArray.map(() => null),
    perspectiveManuallyAdjusted: prev.perspectiveManuallyAdjusted.map(() => false),
    needsInitialization: true, // 觸發重新初始化透視校正點
    imageRotations: newImageRotations,
  };
}

/**
 * 預覽區「重設圖片」(removeProcessedGroup):移除第 groupIndex 組。
 * 該組的原圖、校正圖、細條 URL 交給呼叫端釋放。
 */
export function removeGroupAt(prev: ImageState, groupIndex: number): ImageStateTransition {
  const newProcessedGroups = [...prev.processedGroups];
  const revokeUrls: string[] = [];

  // 釋放該組的物件 URLs
  const groupToRemove = newProcessedGroups[groupIndex];
  if (groupToRemove) {
    // 釋放原始圖片 URL
    if (groupToRemove.originalUrl) {
      revokeUrls.push(groupToRemove.originalUrl);
    }
    // 釋放校正後圖片 URL
    if (groupToRemove.correctedUrl) {
      revokeUrls.push(groupToRemove.correctedUrl);
    }
    // 釋放處理後圖片 URLs
    if (groupToRemove.processedUrls) {
      groupToRemove.processedUrls.forEach(url => {
        if (url) revokeUrls.push(url);
      });
    }
  }

  // 移除該組
  newProcessedGroups.splice(groupIndex, 1);

  // 更新 originalImageCount 以反映當前的圖片數量
  const newOriginalImageCount = newProcessedGroups.length;

  // 重設相關狀態
  const newState = {
    ...prev,
    processedGroups: newProcessedGroups,
    originalImageCount: newOriginalImageCount,
    // 被刪掉的 group 不該留下角度(否則同毫秒重建的新 group 可能撿到舊角度)
    imageRotations: pruneRotations(prev.imageRotations, newProcessedGroups),
    // 旋轉佇列同步(kanban 106497):留著已刪除的 id,視窗會停在一個不存在
    // 的 group —— 序號整個消失、畫布退回 fallback 畫出別張、按旋轉寫進死
    // key(隨後被 pruneRotations 清掉),使用者白轉一輪還以為轉好了
    pendingRotationGroupIds: prev.pendingRotationGroupIds.filter(
      (id) => newProcessedGroups.some((group) => group.id === id)
    ),
    // 四角校正動線的佇列同理(kanban 1003418)
    pendingCorrectionGroupIds: prev.pendingCorrectionGroupIds.filter(
      (id) => newProcessedGroups.some((group) => group.id === id)
    ),
  };

  // 如果沒有組了，也清空其他相關陣列
  if (newProcessedGroups.length === 0) {
    newState.selectedImages = [];
    newState.originalSelectedImages = [];
    newState.previewUrls = [];
    newState.originalPreviewUrls = [];
    newState.perspectivePointsArray = [];
    newState.processedImageUrlArray = [];
    newState.perspectiveManuallyAdjusted = [];
  }

  return { state: newState, revokeUrls };
}
